import { useState } from 'react';
import type { AdminUserListItem, ResetPasswordResponse } from '@/types/api';
import { Dialog } from '@/components/ui/dialog';
import { ConflictError, resetUserPassword } from '../api/admin-users-api';
import { PasswordRevealPanel } from './password-reveal-panel';

type Phase = 'confirm' | 'submitting' | 'reveal';

interface ResetPasswordConfirmProps {
  open: boolean;
  user: AdminUserListItem;
  /** Called after the admin dismisses the reveal panel (i.e. the flow is fully complete). */
  onConfirmed: () => void;
  /** Called when the admin dismisses without confirming, or after the reveal panel closes. */
  onClose: () => void;
}

export function ResetPasswordConfirm({
  open,
  user,
  onConfirmed,
  onClose,
}: ResetPasswordConfirmProps) {
  const [phase, setPhase] = useState<Phase>('confirm');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resetResult, setResetResult] = useState<ResetPasswordResponse | null>(null);

  // NOTE: this component is keyed on `user.id` by its parent, so each new
  // target gets a fresh component instance with reset phase/error state.

  const displayName = `${user.firstName} ${user.lastName}`.trim() || user.email;

  const handleClose = () => {
    if (phase === 'submitting') return;
    onClose();
  };

  const handleConfirm = async () => {
    setErrorMessage(null);
    setPhase('submitting');
    try {
      const result = await resetUserPassword(user.id);
      setResetResult(result);
      setPhase('reveal');
    } catch (err) {
      if (err instanceof ConflictError) {
        setErrorMessage(err.message);
        setPhase('confirm');
        return;
      }
      setErrorMessage('Could not reset password. Check API connectivity and try again.');
      setPhase('confirm');
    }
  };

  const handleRevealConfirm = () => {
    setResetResult(null);
    onConfirmed();
    onClose();
  };

  // Reveal panel uses a non-dismissible Dialog — only exit is the confirm button.
  if (phase === 'reveal' && resetResult) {
    return (
      <Dialog open={open} onClose={handleClose} title="Password reset" dismissible={false}>
        <PasswordRevealPanel
          email={resetResult.email}
          password={resetResult.newPassword}
          onConfirm={handleRevealConfirm}
          testIdPrefix="reset-password-reveal"
        />
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="Reset password"
      dismissible={phase !== 'submitting'}
    >
      <div className="space-y-4" data-testid="reset-password-confirm">
        <p className="text-sm text-gray-300">
          Reset password for <span className="font-semibold text-white">{displayName}</span> (
          <span className="font-mono">{user.email}</span>)? They&apos;ll be required to set a new
          password the next time they sign in. Continue?
        </p>

        {errorMessage && (
          <div
            role="alert"
            data-testid="reset-password-error"
            className="rounded border border-red-500 bg-red-500/10 px-3 py-2 text-sm text-red-300"
          >
            {errorMessage}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            data-testid="reset-password-cancel-button"
            onClick={handleClose}
            disabled={phase === 'submitting'}
            className="rounded border border-gray-600 px-4 py-2 text-sm text-gray-200 hover:bg-gray-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="reset-password-confirm-button"
            onClick={handleConfirm}
            disabled={phase === 'submitting'}
            className="rounded border border-red-700 bg-red-600/10 px-4 py-2 text-sm font-medium text-red-300 hover:bg-red-900/40 disabled:opacity-50"
          >
            {phase === 'submitting' ? 'Resetting…' : 'Reset password'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
